import React from 'react';

import {ActionOrigin, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  STUDY_SCHEMA_CONFIG_ERROR_OPTION,
  STUDY_SCHEMA_ERROR_OPTION,
  STUDY_SCHEMA_NONE_OPTION_ID,
  useStudySchemasAsCreateDropdownOptions,
} from 'studies/sidepanel/CreateStudyMenu';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip, {TooltipProps} from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {withLoadingScreen} from 'util/Promises';

interface NewStudyDropdownButtonParams {
  buttonClassName?: string;
  buttonSize?: ButtonSize;
  buttonText?: string;
  buttonDisabled?: boolean;
  showCaret?: boolean;
  buttonTooltipProps?: Partial<TooltipProps>;
}

export interface CreateStudyModalOptionalParams {
  initialProcedureId?: string | null;
  initialFolderId?: string | null;
}

export default function NewStudyDropdown(
  props: {
    actionOrigin: ActionOrigin;
    studyType?: StudyType;
    studyParams?: CreateStudyModalOptionalParams;
    waitForSave?: () => Promise<void>;
  } & NewStudyDropdownButtonParams
): JSX.Element {
  const dropdownOptions = useStudySchemasAsCreateDropdownOptions(
    props.actionOrigin,
    props.studyParams ?? {},
    props.studyType
  );

  // Build the generic handleClick function for all the menu items
  const handleClickMap = Object.fromEntries(dropdownOptions.map((x) => [x.value, x.handleClick]));
  const handleClick = async (value: string): Promise<void> => {
    const handleClick = handleClickMap[value];
    if (handleClick != null) {
      if (props.waitForSave) {
        await withLoadingScreen(props.waitForSave(), 'Loading...');
      }
      handleClick();
    }
  };

  // If we are displaying a warning message, use the basic menu instead of a searchable menu
  function shouldUseBasicMenu(): boolean {
    return (
      handleClickMap.hasOwnProperty(STUDY_SCHEMA_NONE_OPTION_ID) ||
      handleClickMap.hasOwnProperty(STUDY_SCHEMA_CONFIG_ERROR_OPTION.value) ||
      handleClickMap.hasOwnProperty(STUDY_SCHEMA_ERROR_OPTION.value)
    );
  }

  const menu = shouldUseBasicMenu() ? (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE} className="dropdown-menu pull-right">
      {dropdownOptions.map((option) => (
        <Option
          key={option.name}
          value={option.value}
          onSelect={handleClick}
          icon={option.icon}
          dataTestAttrs={{component: 'NewStudyDropdown', element: 'option'}}
        >
          {option.name}
        </Option>
      ))}
    </Menu>
  ) : (
    <SearchableMenu
      height={Menu.HEIGHTS.MEDIUM}
      width={Menu.WIDTHS.LARGE}
      className="dropdown-menu pull-right"
      onSelect={handleClick}
      optionDatas={dropdownOptions.map((option) => ({
        value: option.value,
        name: option.name,
        icon: option.icon,
        handleClick: option.handleClick,
      }))}
      scrollable={true}
    />
  );

  const button = (
    <Button
      aria-label="Create"
      className={props.buttonClassName}
      dataTestAttrs={{component: 'NewStudyDropdown', element: 'button'}}
      buttonStyle={ButtonStyle.SECONDARY}
      size={props.buttonSize || ButtonSize.SMALL}
      text={props.buttonText}
      disabled={props.buttonDisabled}
      isDropdown={props.showCaret}
      icon="compose"
    />
  );

  return (
    <Dropdown
      menu={menu}
      isOpen={props.buttonDisabled ? false : undefined}
      className="u-display-inline-block dropdown"
    >
      {props.buttonTooltipProps ? (
        <div>
          <Tooltip {...props.buttonTooltipProps}>{button}</Tooltip>
        </div>
      ) : (
        <div>{button}</div>
      )}
    </Dropdown>
  );
}
